import { getBackendUrl } from "@Utils";
import * as Sentry from "@sentry/react";
// Via the alias, not "../axiosWrapper": this module is also bundled by mobile, where
// @AxiosWrapper resolves to mobile's own wrapper. Importing the web one relatively drags
// web's ProviderApp/App/Router into the Metro bundle and uses the wrong store there.
import axiosWrapper from "@AxiosWrapper";
import { resolveFileVersionIds } from "./resolveFileVersionIds";
import type { IsFile403Response } from "@ServerTypes/folders/folders.schema";
import { objectIdString } from "@Helpers/schemas/common.schema";
// Recipient share view (CP-283).
import { getShareViewerSession } from "@Features/external-share/shareViewerSession";
import { getSharedFileAccess } from "@Features/external-share/sharedLink.api";

/**
 * The stored isFile403 result. `Partial` only because the redux slice starts empty
 * — the shape itself is the server's response schema, which is the sole source of
 * truth for it.
 */
export type FileAccessResult = Partial<IsFile403Response>;

// Transmittal-granted access lives in transmittalResourceHasPermission keyed by the
// transmittal's destination space, so both ids must come from the viewer's URL.
function getTransmittalContext() {
  const params = new URLSearchParams(window.location.search);
  const transmittalId = params.get("transmittalId");
  const spaceId = params.get("spaceId");
  return transmittalId ? { transmittalId, spaceId: spaceId ?? undefined } : undefined;
}

/**
 * THE isFile403 call — reports the caller's permission map, whether the file is
 * deleted, and the same per constituent for a merged file.
 *
 * Issued once per file open, by the setAccessPermission thunk only. The result is
 * stored in the accessPermission slice and every other consumer reads it from
 * there, so there is deliberately no cache here.
 */
export async function resolveFileAccess(
  // Only `spaceId` is read, and setAccessPermission deliberately synthesises a one-entry map
  // holding just that — so the parameter is the structural minimum, not the full slice type.
  filesData: Record<string, { spaceId?: string }> | undefined,
  fileVersionId: string,
): Promise<IsFile403Response> {
  // Recipient share view (CP-283): permissions come from the LINK, not from a per-user ACL — there is
  // no account to resolve one against. Branching at this single choke point (the app's only isFile403
  // call) means every downstream gate — `setAccessPermission`, `viewableFileVersionIdsFrom`, the
  // viewer's load gates — is answered correctly for a recipient without any of them knowing about
  // sharing. It also avoids calling the authenticated route, which would 401 and could bounce the
  // recipient to the sign-in page.
  const shareSession = getShareViewerSession();
  if (shareSession) {
    const parsedVersionId = objectIdString.safeParse(fileVersionId);
    if (!parsedVersionId.success) {
      return { status: false, errorMessage: "fileVersionId is not a valid id" };
    }
    const shared = await getSharedFileAccess({
      token: shareSession.token,
      sessionToken: shareSession.sessionToken,
      fileVersionId: parsedVersionId.data,
    }).catch((error: unknown) => {
      Sentry.captureException(error);
      return null;
    });
    if (!shared || !shared.status || !shared.valid) {
      return { status: false, errorMessage: "shared file access check failed" };
    }
    // The recipient's 12h session token can expire MID-VIEW on a password-protected link. That
    // answer arrives as a successful envelope ({status:true, valid:true, requiresPassword:true})
    // carrying no permissions — letting it through here left the viewer permanently blank. Reported
    // as a plain failure instead: the viewer shows its restricted state, and the next navigation
    // re-resolves at page level, which owns the redirect back to the password gate.
    if (shared.requiresPassword) {
      return { status: false, errorMessage: "share session expired" };
    }
    return shared;
  }

  const transmittal = getTransmittalContext();
  const spaceId = transmittal?.spaceId ?? filesData?.[fileVersionId]?.spaceId;
  if (!fileVersionId || !spaceId) return { status: false, errorMessage: "fileVersionId or spaceId missing" };

  try {
    const response = await axiosWrapper.post<IsFile403Response>(`${getBackendUrl()}/folders/isFile403`, {
      fileVersionId,
      spaceId,
      includeConstituentAccess: true,
      ...(transmittal?.transmittalId ? { transmittalId: transmittal.transmittalId } : {}),
    });
    return response.data;
  } catch (error: unknown) {
    Sentry.captureException(error);
    return { status: false, errorMessage: "isFile403 request failed" };
  }
}

/**
 * Ids worth loading model data for. Falls back to the un-gated list when the result
 * carries no `files` — the check failed, or an older server ignored
 * includeConstituentAccess. The server refuses those fetches itself, so this gate
 * only exists to avoid pointless requests.
 */
export function viewableFileVersionIdsFrom(
  access: FileAccessResult | undefined,
  // Forwarded verbatim to resolveFileVersionIds, which owns the entry's shape.
  filesData: Record<string, object> | undefined,
  fileVersionId: string,
): string[] {
  return access?.status === true && access.files
    ? (access.viewableFileVersionIds ?? [])
    : resolveFileVersionIds(filesData, fileVersionId);
}
